import React from "react";
import { motion, useAnimationControls, useReducedMotion } from "framer-motion";

/**
 * Hero‑style strip that matches your top section — now with a smooth carousel.
 * - Same look as before
 * - Names glide horizontally (infinite loop)
 * - Pauses on hover/focus, respects reduced‑motion
 */

const SIGHTS = [
  "Katinkulta",
  "Kuusamon Tropiikki",
  "Saariselkä",
  "Saimaan Rauha",
  "Tampereen Kehräämö",
  "Turun Caribia",
  "Vierumäki",
  "Airisto",
  "Ellivuori",
  "Hannunkivi",
  "Himos",
  "Kalajoki",
  "Kuortane Sport Resort",
  "Naantali Residence",
  "Punkaharju",
  "Pyhä",
  "Pyhäniemi",
  "Salla",
  "Tahko Spa",
  "Ruka",
  "Ylläs",
  "Tuupovaara",
  "Ikaalinen",
  "Imatra Spa",
  "Koli",
  "Laukaa",
  "Levi",
  "Ähtäri",
];

const chipVariants = {
  hidden: { opacity: 0, y: 8 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.15 + i * 0.02, duration: 0.35 },
  }),
};

function Marquee({ items, speed = 45, reverse = false }) {
  const controls = useAnimationControls();
  const prefersReducedMotion = useReducedMotion();

  // Duplicate so we can scroll half the width and loop seamlessly
  const doubled = [...items, ...items, ...items];
  const keyframes = reverse ? ["0%", "-50%"] : ["0%", "-50%"]; // direction handled by flex-row-reverse

  const start = () => {
    if (prefersReducedMotion) return; // respect user setting
    controls.start({
      x: keyframes,
      transition: { duration: speed, ease: "linear", repeat: Infinity },
    });
  };

  const stop = () => controls.stop();

  React.useEffect(() => {
    start();
    return () => controls.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [speed, reverse, prefersReducedMotion]);

  const Row = (
    <motion.ul
      animate={controls}
      style={{ width: "max-content" }}
      className={`no-scrollbar flex ${
        reverse ? "flex-row-reverse" : ""
      } gap-2 sm:gap-3 py-3`}
      /* onMouseEnter={stop}
      onMouseLeave={start}
      onFocusCapture={stop}
      onBlurCapture={start} */
      role="list"
    >
      {doubled.map((name, i) => (
        <li key={`${name}-${i}`} className="snap-start">
          <span className="inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-sm font-medium text-white/90 backdrop-blur-md shadow-sm hover:bg-white/10 hover:shadow transition">
            <span className="mr-2 inline-block h-2 w-2 rounded-full bg-teal-300/90 shadow-[0_0_12px_rgba(94,234,212,0.65)]" />
            {name}
          </span>
        </li>
      ))}
    </motion.ul>
  );

  // Fallback when reduced motion is preferred: static, scrollable row
  if (prefersReducedMotion) {
    return (
      <div
        className="overflow-x-auto no-scrollbar"
        role="region"
        aria-label="Kohdelista, vieritä sivuttain"
      >
        <div className="min-w-full">{Row}</div>
      </div>
    );
  }

  return (
    <div
      className="relative w-full overflow-hidden select-none"
      role="region"
      aria-label="Kohdelista karuselli"
    >
      {Row}
      {/* Edge fades to blend with background */}
      <div className="rounded-2xl pointer-events-none absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-slate-900/70 via-slate-900/40 to-transparent" />
      <div className="rounded-2xl pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-teal-800/40 via-teal-800/30 to-transparent" />
    </div>
  );
}

export default function SightsStrip({}) {
  return (
    <section className="w-full">
      {/* Section background that blends with the hero */}
      <div className="relative mx-auto w-full">
        {/* Gradient band with soft border */}
        <div className="relative rounded-2xl bg-gradient-to-t from-slate-900/70 via-teal-900/50 to-teal-800/40">
          {/* subtle highlight line on top */}
          <div className="rounded-2xl pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

          {/* Carousel chips (same visual, now moving) */}
          <div className="mt-2">
            <Marquee items={SIGHTS} speed={80} />
          </div>
        </div>
      </div>
    </section>
  );
}
